import React from 'react';
import { X, Calendar, Upload, Link2, AlertCircle } from 'lucide-react';

interface CalendarHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendarSource: 'canvas' | 'google' | 'apple' | 'outlook';
}

const CalendarHelpModal: React.FC<CalendarHelpModalProps> = ({ isOpen, onClose, calendarSource }) => {
  if (!isOpen) return null;

  const getInstructions = () => {
    switch (calendarSource) {
      case 'canvas':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-amber-300">📚 Canvas Calendar Instructions</h3>
            
            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-300 mb-2">Step 1: Get Your Canvas Calendar URL</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Log into your Canvas account</li>
                <li>Click on <strong>Calendar</strong> in the left navigation</li>
                <li>Look for calendar settings (usually top-right corner)</li>
                <li>Find <strong>Calendar Feed</strong> option</li>
                <li>Copy the <strong>iCal feed URL</strong></li>
              </ol>
            </div>

            <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-300 mb-2">Step 2: Download .ics File (RECOMMENDED)</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li><strong>Right-click</strong> the iCal feed URL/link</li>
                <li>Select <strong>"Save Link As..."</strong> or <strong>"Download Linked File"</strong></li>
                <li>Save the file (it should end in <code className="bg-black/40 px-1 rounded">.ics</code>)</li>
                <li>In WHIPT, click <strong>"Upload File"</strong> tab</li>
                <li>Upload the downloaded .ics file</li>
                <li>Select which class to add events to</li>
                <li>Choose category (Assignments, Tasks, etc.)</li>
              </ol>
            </div>

            <div className="bg-emerald-900/30 border border-emerald-500/50 rounded-lg p-3">
              <p className="text-sm text-emerald-200">
                <strong>✅ URL Import Fixed!</strong> Canvas calendar URLs now work directly — the request is proxied server-side to bypass CORS restrictions. If you still have issues, try downloading the .ics file and uploading it instead.
              </p>
            </div>
          </div>
        );

      case 'google':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-amber-300">📅 Google Calendar Instructions</h3>
            
            <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-300 mb-2">Method 1: Download .ics File (RECOMMENDED)</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Go to <a href="https://calendar.google.com" target="_blank" className="text-blue-400 underline">Google Calendar</a></li>
                <li>Click the <strong>3 dots</strong> next to your calendar name</li>
                <li>Select <strong>Settings and sharing</strong></li>
                <li>Scroll to <strong>Integrate calendar</strong></li>
                <li>Find the <strong>Secret address in iCal format</strong> link</li>
                <li><strong>Right-click</strong> the iCal link</li>
                <li>Select <strong>"Save Link As..."</strong></li>
                <li>Save the .ics file to your computer</li>
                <li>In WHIPT, use <strong>"Upload File"</strong> tab to upload it</li>
              </ol>
            </div>

            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-300 mb-2">Method 2: Export Full Calendar</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Click Settings (gear icon) → <strong>Settings</strong></li>
                <li>Select your calendar from left sidebar</li>
                <li>Click <strong>Export calendar</strong></li>
                <li>Download the .ics file</li>
                <li>Upload it using the "Upload File" tab</li>
              </ol>
            </div>

            <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
              <p className="text-sm text-red-200">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                <strong>Privacy:</strong> Keep your calendar URL private - anyone with it can see your events!
              </p>
            </div>
          </div>
        );

      case 'apple':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-amber-300">🍎 Apple Calendar Instructions</h3>
            
            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-300 mb-2">Export from Mac Calendar App</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Open <strong>Calendar</strong> app on your Mac</li>
                <li>Select the calendar from the left sidebar</li>
                <li>Go to <strong>File</strong> → <strong>Export</strong> → <strong>Export...</strong></li>
                <li>Save as .ics file to your computer</li>
                <li>In WHIPT, use "Upload File" to upload the .ics file</li>
              </ol>
            </div>

            <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-300 mb-2">iCloud Calendar Link</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Go to <a href="https://www.icloud.com" target="_blank" className="text-blue-400 underline">iCloud.com</a> and sign in</li>
                <li>Open <strong>Calendar</strong></li>
                <li>Click share icon next to a calendar</li>
                <li>Enable <strong>Public Calendar</strong></li>
                <li>Copy the link (change webcal:// to https://)</li>
              </ol>
            </div>
          </div>
        );

      case 'outlook':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-amber-300">📧 Outlook Calendar Instructions</h3>
            
            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-300 mb-2">Method 1: Publish Calendar</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Go to <a href="https://outlook.com" target="_blank" className="text-blue-400 underline">Outlook.com</a> or Outlook Web App</li>
                <li>Click <strong>Settings</strong> (gear) → <strong>View all Outlook settings</strong></li>
                <li>Go to <strong>Calendar</strong> → <strong>Shared calendars</strong></li>
                <li>Under "Publish a calendar", select your calendar</li>
                <li>Click <strong>Publish</strong> and copy the <strong>ICS link</strong></li>
              </ol>
            </div>

            <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-300 mb-2">Method 2: Export .ics File</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>In Outlook Desktop: <strong>File</strong> → <strong>Open & Export</strong> → <strong>Import/Export</strong></li>
                <li>Choose <strong>Export to a file</strong> → <strong>iCalendar Format (.ics)</strong></li>
                <li>Select the calendar and save the file</li>
                <li>Upload it using the "Upload File" tab</li>
              </ol>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 rounded-2xl border-2 border-amber-400/30 shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-yellow-500 p-4 flex items-center justify-between border-b-2 border-amber-700 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-gray-900" />
            <h2 className="text-xl font-bold text-gray-900">Calendar Import Help</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-900" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {getInstructions()}

          {/* Troubleshooting Section */}
          <div className="mt-6 border-t-2 border-gray-700 pt-6">
            <h3 className="text-lg font-bold text-amber-300 mb-4">🛠️ Troubleshooting</h3>
            
            <div className="space-y-3">
              <div className="bg-gray-800/50 rounded-lg p-3">
                <h4 className="font-semibold text-white mb-1">❌ "No events found"</h4>
                <p className="text-gray-400 text-sm">Make sure your calendar has future events. Try a different date range.</p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-3">
                <h4 className="font-semibold text-white mb-1">❌ "Failed to fetch calendar"</h4>
                <p className="text-gray-400 text-sm">Check the URL is correct. Make sure calendar is public/shared. Try uploading .ics file instead.</p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-3">
                <h4 className="font-semibold text-white mb-1">💡 Pro Tip</h4>
                <p className="text-gray-400 text-sm">If URL import doesn't work, download the .ics file from your calendar app and upload it directly!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold py-3 px-6 rounded-lg transition-all"
          >
            Got it! Close Help
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarHelpModal;
